import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext();

const isTokenExpired = (expiryTime) => {
  const currentTime = Date.now();
  return currentTime > expiryTime;
};

// Read persisted auth synchronously so the very first render already has the
// token. (Loading it only inside the effect below delayed App mount — and thus
// the first validate-session / authenticated API call — by a render cycle and
// showed a blank flash.) The same parse + expiry checks are applied here.
const readStoredAuth = () => {
  const data = localStorage.getItem("auth");
  if (!data) {
    return { user: null, accessToken: "" };
  }

  try {
    const parsedData = JSON.parse(data);
    const tokenExpiry = parsedData.accessToken?.expiry;

    if (tokenExpiry && isTokenExpired(tokenExpiry)) {
      localStorage.removeItem("auth");
      return { user: null, accessToken: "" };
    }

    return {
      user: parsedData.user || null,
      accessToken: parsedData.accessToken || "",
    };
  } catch (error) {
    console.error(
      "Failed to parse authentication data from local storage:",
      error,
    );
    return { user: null, accessToken: "" };
  }
};

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(readStoredAuth);
  const [loading, setLoading] = useState(false);

  if (auth?.accessToken?.token) {
    axios.defaults.headers.common["Authorization"] =
      `Bearer ${auth.accessToken.token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }

  useEffect(() => {
    const loadAuthData = () => {
      const data = localStorage.getItem("auth");
      if (data) {
        try {
          const parsedData = JSON.parse(data);

          const tokenExpiry = parsedData.accessToken?.expiry;

          if (tokenExpiry && isTokenExpired(tokenExpiry)) {
            localStorage.removeItem("auth");
            setAuth({
              user: null,
              accessToken: "",
            });
          } else {
            setAuth({
              user: parsedData.user || null,
              accessToken: parsedData.accessToken || "",
            });
          }
        } catch (error) {
          console.error(
            "Failed to parse authentication data from local storage:",
            error,
          );
        }
      }
      setLoading(false);
    };

    loadAuthData();
  }, []);

  useEffect(() => {
    if (loading) return;
    localStorage.setItem("auth", JSON.stringify(auth));
  }, [auth, loading]);

  return (
    <AuthContext.Provider value={[auth, setAuth]}>
      {!loading ? children : null}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

export { AuthProvider, useAuth };
